import React from "react";
import { useSearchParams } from "react-router-dom";

import { CropId, getYearFilters, YearFilter } from "./yield-predictions.utils";

type CropParam = Lowercase<CropId>;

const useYearFilterClickHandler = (availableFilters: YearFilter[]) => {
  const [, setSearchParams] = useSearchParams();

  return React.useCallback(
    (crop: CropParam, index: number) => {
      const filter = availableFilters[index];
      setSearchParams((cur) => {
        cur.set(crop, String(filter.value));
        return cur;
      });
    },
    [availableFilters, setSearchParams],
  );
};

const useActiveYearFilter = (crop: CropParam) => {
  const filters = getYearFilters();
  const [searchParams] = useSearchParams();
  const value = searchParams.get(crop);
  const parsedValue = value ? parseInt(value, 10) : null;

  return filters.find((f) => f.value === parsedValue) ?? filters[0];
};

const useYearFilters = ({ cropId }: { cropId: CropId }) => {
  const filters = React.useMemo(() => getYearFilters(), []);
  const handleFilterClick = useYearFilterClickHandler(filters);
  const cropParam = cropId.toLowerCase() as CropParam;
  const activeFilter = useActiveYearFilter(cropParam);

  return {
    activeFilter,
    filters,
    onClick: (i: number) => handleFilterClick(cropParam, i),
    selectedYear: activeFilter.value,
  };
};

export { useActiveYearFilter, useYearFilterClickHandler, useYearFilters };
